import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gavel, Check, X, PoundSterling, Clock } from "lucide-react";

export default function BiddingDashboard() {
  const { user, checkAuth } = useAuth();
  const [params] = useSearchParams();
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const isBidder = !!user?.is_bidder;
  const until = user?.bidding_until ? new Date(user.bidding_until) : null;

  const loadBids = async () => {
    try {
      const { data } = await api.get("/bids/mine");
      setMyBids(data.items || data || []);
    } catch (e) {
      // silent — user may not have any bids yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBids();
    if (params.get("fee_paid")) {
      toast.success("Admin fee paid — sale finalised.");
      checkAuth?.();
    }
    if (params.get("fee_cancelled")) {
      toast.error("Fee payment cancelled.");
    }
    if (params.get("success")) {
      toast.success("Bidding Access activated!");
      checkAuth?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        lookup_key: "bidding_annual",
        origin_url: window.location.origin + "/dashboard/bidding",
      });
      if (data.url) window.location.href = data.url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start subscription");
    } finally {
      setSubscribing(false);
    }
  };

  const withdraw = async (bidId) => {
    if (!window.confirm("Withdraw this bid?")) return;
    try {
      await api.post(`/bids/${bidId}/withdraw`);
      toast.success("Bid withdrawn");
      loadBids();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to withdraw");
    }
  };

  const confirmPaid = async (bidId) => {
    if (!window.confirm("Confirm the buyer has paid the final agreed price? This will trigger your admin fee payment.")) return;
    try {
      const { data } = await api.post(`/bids/${bidId}/confirm-paid`);
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm payment");
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: "bg-muted text-muted-foreground",
      accepted: "bg-forest text-white",
      awaiting_fee_payment: "bg-safety text-white",
      completed: "bg-forest text-white",
      rejected: "bg-destructive/10 text-destructive",
      withdrawn: "bg-muted text-muted-foreground",
    };
    return <Badge className={map[status] || "bg-muted"}>{status?.replace(/_/g, " ")}</Badge>;
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <div className="flex items-center gap-3">
        <Gavel className="w-7 h-7 text-forest" />
        <h1 className="font-display text-3xl">Dealer Bidding</h1>
      </div>

      {!isBidder ? (
        <div className="mt-8 border border-border bg-white p-8 max-w-lg">
          <div className="overline text-safety">Bidding Access</div>
          <h2 className="mt-2 font-display text-2xl">GBP49.00 / year</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Get unlimited access to place bids on listings as a dealership.
            A 3% admin fee (capped at GBP300 + VAT) applies only when a bid is
            accepted and the final payment is confirmed — paid by the seller.
          </p>
          <Button
            onClick={subscribe}
            disabled={subscribing}
            className="mt-6 w-full rounded-none bg-forest hover:bg-forest-800 text-white h-12"
            data-testid="bidding-subscribe-btn"
          >
            {subscribing ? "Redirecting..." : "Subscribe to Bidding Access"}
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          Bidding Access active{until ? ` until ${until.toLocaleDateString("en-GB")}` : ""}
        </div>
      )}

      <div className="mt-10">
        <div className="overline text-safety mb-4">My bids</div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : myBids.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You haven't placed any bids yet. Browse{" "}
            <Link to="/listings" className="text-forest underline">listings</Link> to get started.
          </p>
        ) : (
          <div className="divide-y divide-border border border-border bg-white">
            {myBids.map((b) => (
              <div key={b.bid_id} className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium">{b.listing_title || b.listing_id}</div>
                  <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <PoundSterling className="w-3.5 h-3.5" /> {Number(b.amount).toLocaleString("en-GB")}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {statusBadge(b.status)}
                  {b.status === "pending" && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => withdraw(b.bid_id)}>
                      <X className="w-4 h-4 mr-1" /> Withdraw
                    </Button>
                  )}
                  {b.status === "accepted" && (
                    <Button size="sm" className="rounded-none bg-forest text-white hover:bg-forest-800" onClick={() => confirmPaid(b.bid_id)}>
                      <Check className="w-4 h-4 mr-1" /> Confirm paid
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
